import { createSlice } from '@reduxjs/toolkit';

export interface ImageSubmissionStatus {
    imageId: string;
    status: string;
    reason?: string;
}

interface ImageState {
    selectedImageIds: string[];
    imagesToBeSubmittedForAnalysis: string[];
    imageSubmissionStatusList: ImageSubmissionStatus[];
    modelName: string | null;
    props: any;
    listVersion: number;
}

const initialState: ImageState = {
    selectedImageIds: [],
    imagesToBeSubmittedForAnalysis: [],
    imageSubmissionStatusList: [],
    modelName: null,
    props: {},
    listVersion: 0,
};

export const imageSlice = createSlice({
    name: 'image',
    initialState,
    reducers: {
        selectImageIds: (state, action) => {
            state.selectedImageIds = [...action.payload]
        },
        clearSelectedImageIds: (state) => {
            state.selectedImageIds = [];
        },
        setImageToBeSubmittedForAnalysis: (state, action) => {
            state.imagesToBeSubmittedForAnalysis = [...action.payload]
        },
        setImageSubmissionStatusList: (state, action) => {
            state.imageSubmissionStatusList = [...action.payload]
        },
        // Seeds the whole status list at once from a batch-analyze response's
        // results[] (accepted/reason per image), replacing the old
        // per-item status-update dispatched from inside a request loop.
        setImageSubmissionStatusListFromResults: (state, action) => {
            state.imageSubmissionStatusList = action.payload.map(
                (result: { imageId: string; accepted: boolean; reason?: string }) => ({
                    imageId: result.imageId,
                    status: result.accepted ? 'success' : 'error',
                    reason: result.reason,
                })
            );
        },
        clearSubmissionLists: (state) => {
            state.imagesToBeSubmittedForAnalysis = [];
            state.imageSubmissionStatusList = [];
        },
        setModelName: (state, action) => {
            state.modelName = action.payload;
        },
        clearModelName: (state) => {
            state.modelName = null;
        },
        // bump to force the accumulating image list to reset+refetch (e.g. after trash)
        bumpImageListVersion: (state) => {
            state.listVersion += 1;
        },
    },
});

export const { 
    selectImageIds,
    clearSelectedImageIds,
    setImageToBeSubmittedForAnalysis,
    setImageSubmissionStatusList,
    setImageSubmissionStatusListFromResults,
    clearSubmissionLists,
    setModelName,
    clearModelName,
    bumpImageListVersion,
} = imageSlice.actions;

export const selectImageIdsState = (state: any) => state.image.selectedImageIds;
export const selectImageListVersion = (state: any) => state.image.listVersion;
export const selectModelName = (state: any) => state.image.modelName;
export const selectImagesToBeSubmittedForAnalysis = (state: any) => state.image.imagesToBeSubmittedForAnalysis;
export const selectImageSubmissionStatusList = (state: any) => state.image.imageSubmissionStatusList;

export default imageSlice.reducer;
